import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';

import { combineLatest, firstValueFrom, interval, Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';

import { ModalsSelectors } from 'src/app/features/modals';
import { ProductType } from 'src/app/features/packages/models';
import { PackagesSelectors } from 'src/app/features/packages/store/selectors';
import { ParticipantVerificationSelectors } from 'src/app/features/participant-verification/store/selectors';
import { RootStoreState } from 'src/app/store';

import { ModalsActions } from '../../../modals/store/actions';
import { VideoIssueModals } from '../../enums';
import { VideoConnectionModel } from '../../models';
import { VideoActions, VideoSelectors } from '../../store';
import { AudioVideoFailureModalComponent } from '../audio-video-failure-modal';
import { DeviceInUseModalComponent } from '../device-in-use-modal/device-in-use-modal.component';
import { RefreshNeededModalComponent } from '../refresh-needed-modal/refresh-needed-modal.component';
import { VideoProblemsWithDeviceModalComponent } from '../video-device-issues-modal';
import { VideoSignalDegradedModalComponent } from '../video-signal-degraded-modal/video-signal-degraded-modal.component';
import { AudioVideoConnectionMonitorService } from 'src/app/features/audio-video-connection-monitor/services/audio-video-connection-monitor.service';
import { NavigatorMediaDeviceWrapperService } from 'src/app/features/audio-video-connection-monitor/services/navigator-media-device-wrapper.service';

@Component({
    selector: 'app-video-issue',
    template: '',
    standalone: false
})
export class VideoIssueComponent implements OnInit, OnDestroy {
  private readonly audioVideoConnectionMonitorService = inject(AudioVideoConnectionMonitorService);
  private readonly navigatorMediaDeviceWrapperService = inject(NavigatorMediaDeviceWrapperService);

  notifier = new Subject();
  pollingInterval$ = interval(5000).pipe(
    tap(() => {
      this.store.dispatch(VideoActions.GetAreAllVideoStreamsConnected());

      combineLatest([
        this.store.select(VideoSelectors.getVideoConnection),
        this.store.select(VideoSelectors.getAreAllVideoStreamsConnected),
        this.store.select(VideoSelectors.getStopVideo),
        this.store.select(ModalsSelectors.getModalComponent),
        this.store.select(ParticipantVerificationSelectors.doesIdVerificationExist),
        this.store.select(PackagesSelectors.getProductType),
      ])
        .pipe(
          take(1),
          filter(
            ([videoConnection, areAllVideoStreamsConnected, stopVideo, modalComponent]) =>
              !!videoConnection && !areAllVideoStreamsConnected && !stopVideo && !modalComponent
          ),
          tap(
            async ([
              videoConnection,
              _,
              __,
              ___,
              isParticipantVerificationPresent,
              productType,
            ]) => {
              const modalType = await this.getModalType(videoConnection);
              if (
                modalType &&
                (isParticipantVerificationPresent || productType === ProductType.KRON)
              ) {
                this.store.dispatch(modalType);
              }
            }
          )
        )
        .subscribe();
    }),
    takeUntil(this.notifier)
  );

  constructor(
    private readonly store: Store<RootStoreState.State>,
  ) {}

  ngOnInit() {
    this.pollingInterval$.subscribe();
  }

  async getModalType(videoConnection: VideoConnectionModel) {
    if (!!videoConnection && !videoConnection.isConnected) {
      switch (videoConnection.modalType) {
        case VideoIssueModals.DeviceInUse: {
          return ModalsActions.SetStandaloneModalComponent({
            payload: {
              component: DeviceInUseModalComponent,
              componentData: { isPublishingError: videoConnection.isPublisher },
            },
          });
        }
        case VideoIssueModals.VideoSignalDegraded: {
          return ModalsActions.SetStandaloneModalComponent({
            payload: {
              component: VideoSignalDegradedModalComponent,
            },
          });
        }
        case VideoIssueModals.AudioVideoFailure: {
          return ModalsActions.SetStandaloneModalComponent({
            payload: {
              component: AudioVideoFailureModalComponent,
              componentData: { isPublishingError: videoConnection.isPublisher },
            },
          });
        }
        case VideoIssueModals.RefreshNeeded: {
          return ModalsActions.SetStandaloneModalComponent({
            payload: {
              component: RefreshNeededModalComponent,
              componentData: { isPublishingError: videoConnection.isPublisher },
            },
          });
        }
        default: {
          const payload = {
            component: VideoProblemsWithDeviceModalComponent,
            componentData: { isPublishingError: videoConnection.isPublisher },
          };

          return this.audioVideoConnectionMonitorService.shouldDeviceChangeDispatchModal(
            await firstValueFrom(this.navigatorMediaDeviceWrapperService.enumerateDevices())
          )
            ? ModalsActions.SetStandaloneModalComponent({
                payload,
              })
            : null;
        }
      }
    }
    return undefined;
  }

  ngOnDestroy() {
    this.notifier.next(undefined);
    this.notifier.complete();
  }
}
